import { useMemo } from "react"
import { useWidget } from "../../../providers/widget"
import { hostFontSizeCss } from "../../../common/host-scope"
import { ReviewsWidgetConfig } from "../config"

/**
 * Dark accent overrides for the auto FIRST-PAINT path (#900). The base template
 * always emits the LIGHT accent (so light output stays byte-identical); these
 * append the DARK accent under two selector prefixes:
 *   - `:host([data-hr-theme="dark"])` — the runtime marker (explicit dark +
 *     post-hydration auto), plus the mirrored elevated-dialog host.
 *   - inside `@media (prefers-color-scheme: dark)`,
 *     `:host([data-hr-scheme="auto"]:not([data-hr-theme]))` — a server-stamped
 *     auto widget on a dark OS, painting dark AT PARSE with no JS/cookie; the
 *     `:not` drops it the instant the runtime stamps the marker at hydration.
 * The dark accent is read from `darkModeColors.accent` DIRECTLY (not gated on
 * `resolvedTheme`) so it ships on every render — auto first paint can't wait
 * for the resolved theme to flip. `:host(...)` (not a descendant selector) is
 * required to reach the shadow host from inside its own stylesheet.
 */
function reviewsDarkAccentOverrides(prefix: string, darkAccent: string): string {
  return `
    ${prefix} .text-accent {
      color: ${darkAccent} !important;
    }
    ${prefix} .bg-accent {
      background-color: ${darkAccent} !important;
    }
  `
}

export function useReviewsCss(): string {
  const { options, resolvedTheme, widgetId } = useWidget<ReviewsWidgetConfig>()

  const colors = useMemo(() => {
    return resolvedTheme === 'dark' ? options.darkModeColors : options.lightModeColors
  }, [resolvedTheme, options.lightModeColors, options.darkModeColors])

  return useMemo(() => `
    ${colors.accent ? `
    .text-accent {
      color: ${colors.accent} !important;
    }
    .bg-accent {
      background-color: ${colors.accent} !important;
    }
    ` : ''}
    .homerunner-reviews-container *, .homerunner-review-details-modal *, .homerunner-all-reviews-modal * {
      font-family: '${options.font.family}' !important;
    }
    ${hostFontSizeCss(widgetId, options.font.size, { cancelHostPaint: false })}
    ${options.darkModeColors.accent ? `
    ${reviewsDarkAccentOverrides(':where(:host([data-hr-theme="dark"]))', options.darkModeColors.accent)}
    @media (prefers-color-scheme: dark) {
      ${reviewsDarkAccentOverrides(':where(:host([data-hr-scheme="auto"]:not([data-hr-theme])))', options.darkModeColors.accent)}
    }
    ` : ''}
    ${options.customCss ?? ''}
  `, [widgetId, colors.accent, options.font.family, options.font.size, options.darkModeColors.accent, options.customCss])
}
